'use client';

import { ColumnDef, Row } from '@tanstack/react-table';
import { Histogram, hasHistogramData } from '@/components/Shared/Charts/Histogram';
import { NotAvailable } from '@/components/Shared/NotAvailable';
import { formatDecimal } from '@/lib/number';
import { TableColumnStatistics } from '@/lib/types/TableColumnStatistics';
import { TableStatsHistogram } from '@/lib/types/TableStatsHistogram';
import { percentageVisualization } from '../helper';
type CellFormat = 'text' | 'number' | 'percentage' | 'percentage_inverted';
type CellValue = string | number | null | undefined;
interface FormatOptions {
  type: CellFormat;
  alignment?: string;
}
const formatNumber = (value: number, type: CellFormat) => {
  switch (type) {
    case 'number':
      {
        return formatDecimal(value);
      }
    case 'percentage':
    case 'percentage_inverted':
      {
        return percentageVisualization(value, type === 'percentage_inverted');
      }
    default:
      {
        return value;
      }
  }
};
function formatCell<T>(row: Row<T>, columnName: string, options: FormatOptions = {
  type: 'text',
  alignment: 'text-left'
}): JSX.Element {
  const {
    type
  } = options;
  const value = row.getValue<CellValue>(columnName);
  const number = Number(value);
  const isNumber = !Number.isNaN(number);
  const isMissing = value === null || value === undefined;
  const formattedValue = isNumber && type !== 'text' ? formatNumber(number, type) : value;
  return <div>
      {isMissing && <NotAvailable />}
      {!isMissing && formattedValue}
    </div>;
}
export const columns: ColumnDef<TableColumnStatistics & {
  id?: string;
  histogramData?: TableStatsHistogram;
}>[] = [{
  accessorKey: 'name',
  header: 'Name'
}, {
  accessorKey: 'datatype',
  header: 'Datatype'
}, {
  accessorKey: 'description',
  header: 'Description',
  enableResizing: true,
  size: 500
}, {
  accessorKey: 'histogram',
  header: 'Histogram',
  cell: ({
    row
  }) => {
    const data = row.original.histogram || row.original.length_histogram;
    if (!hasHistogramData(data)) {
      return <div className="inline-flex w-full h-full items-center">
            <NotAvailable />
          </div>;
    }
    return <div className="inline-flex w-full h-full items-center justify-center">
          <Histogram data={data} type="tiny" />
        </div>;
  }
}, {
  accessorKey: 'missing',
  header: 'Missing %',
  cell: ({
    row
  }) => formatCell(row, 'missing', {
    type: 'percentage_inverted'
  })
}, {
  accessorKey: 'distinct',
  header: 'Unique Values',
  cell: ({
    row
  }) => formatCell(row, 'distinct', {
    type: 'number'
  })
}, {
  accessorKey: 'unique',
  header: 'Unique %',
  cell: ({
    row
  }) => formatCell(row, 'unique', {
    type: 'percentage'
  })
}, {
  accessorKey: 'minimum',
  header: 'Min',
  cell: ({
    row
  }) => formatCell(row, 'minimum', {
    type: 'number'
  })
}, {
  accessorKey: 'maximum',
  header: 'Max',
  cell: ({
    row
  }) => formatCell(row, 'maximum', {
    type: 'number'
  })
}, {
  accessorKey: 'mean',
  header: 'Mean',
  cell: ({
    row
  }) => formatCell(row, 'mean', {
    type: 'number'
  })
}, {
  accessorKey: 'median',
  header: 'Median',
  cell: ({
    row
  }) => formatCell(row, 'median', {
    type: 'number'
  })
}, {
  accessorKey: 'stdev',
  header: 'S.Deviation',
  cell: ({
    row
  }) => formatCell(row, 'stdev', {
    type: 'number'
  })
}];